import { createAsyncThunk } from "@reduxjs/toolkit";
import apiCall from "../AuthorizedApi";
import { showErrorToast } from "../../../components/atoms/Utlis/Toast";
import axios from "axios";
import { config, env } from "../../../config/config";

import {
  AlbumManualPaymentPayload,
  StudioUpiDetails,
  UpiQrPaymentPayload,
  UpiQrPaymentResponse,
} from "../../../Data/transaction.dto";

export const createCustomerPaymentLinkApi = async ({
  amount,
  first_name,
  last_name,
  customer_name,
  contact_details,
  description,
  host_name,
  payment_provider,
  album_id,
  email,
  return_url,
  use_extra_payment_link,
}: {
  amount: number;
  first_name?: string;
  last_name?: string;
  customer_name?: string;
  contact_details?: string;
  description?: string;
  host_name?: string;
  payment_provider?: "cashfree" | "razorpay";
  album_id?: number;
  email?: string;
  return_url?: string;
  use_extra_payment_link?: boolean;
}) => {
  try {
    const payload: any = {
      amount,
      host_name: host_name || window.location.origin,
      ...(payment_provider ? { payment_provider } : {}),
      ...(customer_name ? { customer_name } : {}),
      ...(first_name ? { first_name } : {}),
      ...(last_name ? { last_name } : {}),
      ...(contact_details ? { contact_details } : {}),
      ...(description ? { description } : {}),
      ...(album_id ? { album_id } : {}),
      ...(email ? { email } : {}),
      ...(return_url ? { return_url } : {}),
      ...(typeof use_extra_payment_link === "boolean" ? { use_extra_payment_link } : {}),
    };

    const response = await apiCall({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      url: `/plans/create-payment-link/`,
      data: payload,
    });
    // console.log(response);
    return response;
  } catch (error: any) {
    console.log("error", error);
    const msg = error?.response?.data?.message || error?.response?.data?.error || "Error in payment link generation.";
    showErrorToast(msg);
    return null;
  }
};

/** Public API — no auth required. Fetches checkout session for customer pay page. */
export const getCustomerPaymentCheckout = async (secretToken: string) => {
  try {
    const response = await axios.get(
      `${config[env].BACKEND_URL}/plans/customer-payment/${secretToken}/checkout/`
    );
    return response.data;
  } catch (error: any) {
    console.log("error", error);
    return null;
  }
};

/** Studio-auth API — confirms payment status after Cashfree checkout. */
export const checkPaymentStatusV2 = async (token: string) => {
  try {
    const response = await apiCall({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      url: `/plans/check-payment-status_v2/`,
      data: { token },
    });
    return response;
  } catch (error: any) {
    console.log("error", error);
    showErrorToast("Error checking payment status.");
    return null;
  }
};
export const addManualPaymentAPI = async ({
  amount,
  first_name,
  last_name,
  contact_details,
  description,
  host_name,
  payment_method,
}: {
  amount: number;
  first_name: string;
  last_name: string;
  contact_details: string;
  description: string;
  host_name: string;
  payment_method: string;
}) => {
  try {
    const response = await apiCall({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      url: `/plans/admin/manual-booking-payment/`,
      data: {
        amount,
        first_name,
        last_name,
        contact_details,
        description,
        host_name,
        payment_method,
      },
    });
    // console.log(response);
    return response;
  } catch (error: any) {
    console.log("error", error);
    showErrorToast("Error in payment link generation.");
    return null;
  }
};

export const completePaymentAPI = async (token: string) => {
  try {
    const response = await apiCall({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      url: `/plans/check-payment-status/`,
      data: {
        token,
      },
    });
    // console.log(response);
    return response;
  } catch (error: any) {
    console.log("error", error);
    showErrorToast("Error in payment completion.");
    return null;
  }
};

export const getAlbumCustomersAPI = async ({
  search,
  page = 1,
  page_size = 20,
}: {
  search?: string;
  page?: number;
  page_size?: number;
} = {}) => {
  try {
    const params = new URLSearchParams();
    if (search && search.trim()) params.append("search", search.trim());
    if (page) params.append("page", String(page));
    if (page_size) params.append("page_size", String(page_size));

    const response = await apiCall({
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      url: `/plans/album-customers/?${params.toString()}`,
    });
    return response;
  } catch (error: any) {
    console.log("error", error);
    return null;
  }
};

export const createAlbumManualPaymentAPI = async ({
  album_id,
  amount,
  payment_method,
  payment_status = "Completed",
  customer_name,
  contact_details,
  transaction_reference,
  description,
}: AlbumManualPaymentPayload) => {
  try {
    const response = await apiCall({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      url: `/plans/create-album-manual-payment/`,
      data: {
        album_id,
        amount,
        payment_method,
        payment_status,
        ...(customer_name ? { customer_name } : {}),
        ...(contact_details ? { contact_details } : {}),
        ...(transaction_reference ? { transaction_reference } : {}),
        ...(description ? { description } : {}),
      },
    });
    return response;
  } catch (error: any) {
    console.log("error", error);
    const msg =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Failed to record manual payment.";
    showErrorToast(msg);
    return null;
  }
};

export const createManualCustomerPaymentAPI = async ({
  amount,
  payment_method,
  contact_details,
  platform_fee,
  album_id,
  transaction_reference,
  first_name,
  last_name,
  customer_name,
  description,
  preview_only,
}: {
  amount: number;
  payment_method: string;
  contact_details?: string;
  platform_fee?: number;
  album_id?: number;
  transaction_reference?: string;
  first_name?: string;
  last_name?: string;
  customer_name?: string;
  description?: string;
  preview_only?: boolean;
}) => {
  try {
    const finalCustomerName =
      customer_name || (first_name ? `${first_name} ${last_name || ""}`.trim() : undefined);
    const payload: any = {
      amount,
      payment_method,
      ...(contact_details ? { contact_details } : {}),
      ...(platform_fee !== undefined ? { platform_fee } : {}),
      ...(album_id ? { album_id } : {}),
      ...(transaction_reference ? { transaction_reference } : {}),
      ...(first_name ? { first_name } : {}),
      ...(last_name ? { last_name } : {}),
      ...(finalCustomerName ? { customer_name: finalCustomerName } : {}),
      ...(description ? { description } : {}),
      ...(preview_only !== undefined ? { preview_only } : {}),
    };

    const response = await apiCall({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      url: `/plans/create-manual-payment/`,
      data: payload,
    });
    return response;
  } catch (error: any) {
    console.log("error", error);
    const msg =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Failed to record manual payment.";
    showErrorToast(msg);
    return null;
  }
};

export const getAlbumPaymentHistoryAPI = async ({
  album_id,
  status,
  page = 1,
  page_size = 20,
}: {
  album_id: number;
  status?: string;
  page?: number;
  page_size?: number;
}) => {
  try {
    const params = new URLSearchParams();
    params.append("album_id", String(album_id));
    if (status && status.trim()) params.append("status", status.trim());
    if (page) params.append("page", String(page));
    if (page_size) params.append("page_size", String(page_size));

    const response = await apiCall({
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      url: `/plans/album-payment-history/?${params.toString()}`,
    });
    return response;
  } catch (error: any) {
    console.log("error", error);
    return null;
  }
};

export const getCustomerTransactionAPI = createAsyncThunk(
  "payments/getCustomerTransactionAPI",
  async (
    filterParams?: {
      search?: string;
      status?: string;
      album_id?: number;
      album_only?: boolean;
      general_only?: boolean;
      payment_type?: "general" | "album";
      page?: number;
      page_size?: number;
    }
  ) => {
    try {
      const params = new URLSearchParams();
      if (filterParams?.search && filterParams.search.trim()) {
        params.append("search", filterParams.search.trim());
      }
      if (filterParams?.status) params.append("status", filterParams.status);
      if (filterParams?.album_id) params.append("album_id", String(filterParams.album_id));
      if (filterParams?.album_only) params.append("album_only", "true");
      if (filterParams?.general_only) params.append("general_only", "true");
      if (filterParams?.payment_type) params.append("payment_type", filterParams.payment_type);
      if (filterParams?.page) params.append("page", String(filterParams.page));
      if (filterParams?.page_size) params.append("page_size", String(filterParams.page_size));

      const queryString = params.toString() ? `?${params.toString()}` : "";
      const response = await apiCall({
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        url: `/plans/fetch_customer_payments/${queryString}`,
      });
      // console.log(response);
      return response;
    } catch (error: any) {
      return null;
    }
  }
);

/**
 * Direct UPI APIs
 */

export const getStudioUpiVpaAPI = async (): Promise<{
  status: boolean;
  message?: string;
  data?: StudioUpiDetails;
} | null> => {
  try {
    const response = await apiCall({
      method: "GET",
      url: `/account/studio/upi-vpa/`,
    });
    return response;
  } catch (error: any) {
    console.error("Error fetching studio UPI details:", error);
    return null;
  }
};

export const saveStudioUpiVpaAPI = async (payload: {
  upi_vpa: string;
  upi_payee_name?: string;
}): Promise<{
  status: boolean;
  message?: string;
  data?: StudioUpiDetails;
  error?: string;
}> => {
  try {
    const response = await apiCall({
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      url: `/account/studio/upi-vpa/`,
      data: payload,
    });
    return response;
  } catch (error: any) {
    console.error("Error saving studio UPI ID:", error);
    const msg =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Failed to save UPI ID. Expected format like name@bank.";
    showErrorToast(msg);
    return {
      status: false,
      message: msg,
      error: msg,
    };
  }
};

export const deleteStudioUpiVpaAPI = async (): Promise<{
  status: boolean;
  message?: string;
  data?: StudioUpiDetails;
} | null> => {
  try {
    const response = await apiCall({
      method: "DELETE",
      url: `/account/studio/upi-vpa/`,
    });
    return response;
  } catch (error: any) {
    console.error("Error removing studio UPI ID:", error);
    const msg =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Failed to remove UPI ID.";
    showErrorToast(msg);
    return null;
  }
};

export const createUpiQrPaymentAPI = async (
  payload: UpiQrPaymentPayload
): Promise<(UpiQrPaymentResponse & { error?: string; code?: string }) | null> => {
  try {
    const response = await apiCall({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      url: `/plans/upi-qr/`,
      data: payload,
    });
    return response;
  } catch (error: any) {
    console.error("Error generating direct UPI QR:", error);
    const errData = error?.response?.data;
    const msg =
      errData?.error ||
      errData?.message ||
      "Failed to generate UPI QR. Please try again.";
    const code = errData?.code;
    showErrorToast(msg);
    return { error: msg, code } as any;
  }
};

export const getUpiQrPaymentAPI = async (
  paymentId: number | string
): Promise<UpiQrPaymentResponse | null> => {
  try {
    const response = await apiCall({
      method: "GET",
      url: `/plans/upi-qr/${paymentId}/`,
    });
    return response;
  } catch (error: any) {
    console.error("Error fetching UPI QR payment details:", error);
    return null;
  }
};

export const confirmUpiQrPaymentAPI = async (
  paymentId: number | string,
  data: {
    status?: "paid" | "failed" | "expired";
    utr?: string;
  }
): Promise<(UpiQrPaymentResponse & { error?: string; code?: string }) | null> => {
  try {
    const response = await apiCall({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      url: `/plans/upi-qr/${paymentId}/confirm/`,
      data,
    });
    return response;
  } catch (error: any) {
    console.error("Error confirming UPI payment:", error);
    const errData = error?.response?.data;
    const msg =
      errData?.error ||
      errData?.message ||
      "Failed to confirm UPI payment.";
    const code = errData?.code;
    showErrorToast(msg);
    return { error: msg, code } as any;
  }
};

